import PromotionRuleI = Models.PromotionRuleI

/**
 * Normalise a user-typed bid/spend value to a clean Number, or null if
 * unparseable.
 *
 * Why this exists
 * ---------------
 * The backend's `Promotion::formatBid` does `100 * $value` with no
 * normalisation. If a non-numeric string reaches it (e.g. `"0,35"` from a
 * Spanish-locale user, ref Bugsnag event 6a08b6b18c3285d1a592033d, 2026-05-16
 * POST /api/v1/promotions, 5 occurrences from userId 2965 in Spain), PHP
 * raises "A non-numeric value encountered" and the request 500s. We must
 * normalise here so the backend only ever sees a clean Number or null.
 *
 * Why "last separator wins" instead of `Intl.NumberFormat.formatToParts`
 * ---------------------------------------------------------------------
 * `formatToParts` is the textbook locale-aware approach, but it requires
 * `navigator.language` to match the convention the user is actually typing
 * in. When it doesn't (Spanish user on a colleague's en-US browser, or a
 * paste from a EU source into a US locale), the parse can silently 100×
 * a value: en-US says `,` is the group separator, so `"0,35"` becomes `35`
 * — a $35 bid instead of $0.35. For a field that controls real ad spend
 * that's a much worse failure than the one ambiguity this heuristic accepts.
 *
 * Heuristic: treat whichever of `,` or `.` appears LAST as the decimal
 * separator. Any earlier `,` or `.` is treated as a thousands separator and
 * stripped. This gives the right answer for both `"1,234.56"` (US) and
 * `"1.234,56"` (EU) without needing to know the user's locale.
 *
 * The one ambiguity: bare `"1,234"` is read as `1.234`. For a bid amount
 * (almost always under $100) the small-number reading is the safer default
 * — a typo costing $1.234 truncates harmlessly; a typo costing $1234 burns
 * a budget. If a true currency/budget field is added later that legitimately
 * accepts ≥ 1000, switch to a locale-aware parser there.
 *
 * Apply to every field that `Promotion::formatBid` touches in
 * `Promotion::syncRules`: bid_increase_amount, bid_increase_percent, bid_set,
 * spend_value.
 */
export const parseFloatAndCoerce = (
    object: PromotionRuleI,
    key: 'bid_increase_amount' | 'bid_increase_percent' | 'bid_set' | 'spend_value',
) => {
    if (!Object(object).hasOwnProperty(key)) return
    ;(object as any)[key] = coerceBidValue((object as any)[key])
}

/**
 * Exposed for direct testing. Returns a clean Number for any parseable input,
 * null for empty / null / unparseable values.
 */
export const coerceBidValue = (raw: unknown): number | null => {
    if (raw === null || raw === undefined) return null
    if (typeof raw === 'number') return isFinite(raw) ? raw : null

    // Strip whitespace and the currency / percent markers users sometimes
    // paste in. Do this first so separator counting isn't fooled by spacing.
    const cleaned = String(raw).replace(/[\s%$]/g, '')
    if (cleaned === '') return null

    const lastComma = cleaned.lastIndexOf(',')
    const lastDot = cleaned.lastIndexOf('.')

    let normalised: string
    if (lastComma === -1 && lastDot === -1) {
        // No separator at all (`"35"`, `"0"`).
        normalised = cleaned
    } else {
        // Whichever separator appears LAST is the decimal. Any earlier `,`
        // or `.` is treated as a thousands separator and dropped.
        const decimalIdx = Math.max(lastComma, lastDot)
        const intPart = cleaned.slice(0, decimalIdx).replace(/[,.]/g, '')
        const fracPart = cleaned.slice(decimalIdx + 1)
        normalised = `${intPart}.${fracPart}`
    }

    const value = parseFloat(normalised)
    return isNaN(value) ? null : value
}
